import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionStatus } from 'thunkless';
import _ from 'lodash';
import { detectOS } from 'detect-browser';

import {
  ticketEventsOrderedListSelectorFactory,
  ticketEventsOrderedRenderIdsSelectorFactory,
  ticketFetchPreviousTicketsStatusSelector,
  ticketIdSelector,
  ticketPreviousTicketsRenderIdSelector,
} from '@/redux/app/selectors/ticket';
import { eventsForTicketLastKeySelector, eventsForTicketLoadingStatusSelector } from '@/redux/app/selectors/event';
import { fetchEvents } from '@/redux/app/actions/event';
import { fetchPreviousTickets } from '@/redux/app/actions/ticket';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';
import { useCachedValue } from '@/lib/react-hooks';

import { UILoader } from '../../../ui-elements/UILoader';
import * as ReverseInfiniteList from '../../../shared/ReverseInfiniteList';
import { Event, EventInfoContext } from '../../../event';

import { PreviousTickets } from './PreviousTickets';
import { ErrorDetailModal } from './ErrorDetailModal';
import { BackToSetup } from '../../../onboarding/BackToSetup';

export interface TicketEventsProps {
  ticket: string;
}

const PREVIOUS_KEY = 'previous';

const isIos = detectOS(window.navigator.userAgent) === 'iOS';

export const TicketEvents = ({ ticket }: TicketEventsProps) => {
  const dispatch = useDispatch();

  const { subdomain } = useContext(NavigationPaneRouteParamsContext);
  const ticketId = useSelector(_.partial(ticketIdSelector, ticket));
  const lastKey = useSelector(_.partial(eventsForTicketLastKeySelector, ticket));
  const loadingStatus = useSelector(_.partial(eventsForTicketLoadingStatusSelector, ticket));
  const previousTicketsFetchStatus = useSelector(
    _.partial(ticketFetchPreviousTicketsStatusSelector, ticket),
  );
  const previousTicketsRenderId = useSelector(
    _.partial(ticketPreviousTicketsRenderIdSelector, ticket),
  );

  const dataSelector = useMemo(
    () => ticketEventsOrderedListSelectorFactory(ticket),
    [ticket, lastKey],
  );
  const data = useSelector(dataSelector);

  const renderIdsSelector = useMemo(() => (
    ticketEventsOrderedRenderIdsSelectorFactory(ticket)
  ), [ticket]);
  const renderIds = useSelector(renderIdsSelector);
  const [activeError, setError] = useState('');

  const updateError = (value) => {
    setError(value);
  };

  // Batch the last page of events with previous tickets to prevent extra jankiness on iOS
  const [cachedData, cachedRenderIds] = isIos ? useCachedValue(
    () => [data, renderIds],
    !!lastKey || loadingStatus !== ActionStatus.SUCCESS || (
      !!previousTicketsFetchStatus && previousTicketsFetchStatus !== ActionStatus.BUSY
    ),
  ) : [data, renderIds];

  const dataWithPreviousTickets = useMemo(
    () => (
      previousTicketsFetchStatus === ActionStatus.SUCCESS ? [PREVIOUS_KEY] : []
    ).concat(cachedData || []),
    [cachedData, previousTicketsFetchStatus],
  );
  const renderIdsWithPreviousTickets = useMemo(
    () => (
      previousTicketsRenderId && previousTicketsFetchStatus === ActionStatus.SUCCESS
        ? [previousTicketsRenderId]
        : []
    ).concat(cachedRenderIds || []),
    [cachedRenderIds, previousTicketsRenderId, previousTicketsFetchStatus],
  );

  const renderEvent = useCallback((eventId: string) => (
    eventId === PREVIOUS_KEY
      ? <div className="event-final-box"><PreviousTickets ticket={ticket} /></div>
      : <Event id={eventId} ticket={ticket} />
  ), [ticket]);

  const loadNextPage = useCallback(() => {
    if (!ticketId) return;
    if (lastKey) {
      dispatch(fetchEvents({ ticketUuid: ticket, ticketId, subdomain, limit: 50, after: lastKey }));
    } else if (!previousTicketsFetchStatus || previousTicketsFetchStatus === ActionStatus.FAILURE) {
      dispatch(fetchPreviousTickets({ ticketUuid: ticket, ticketId, subdomain }));
    }
  }, [ticket, ticketId, lastKey, subdomain, previousTicketsFetchStatus]);

  if (isIos) {
    useEffect(() => {
      if (data !== cachedData) loadNextPage();
    }, [lastKey]);
  }

  useEffect(() => {
    if (!ticketId) return;
    const p: any = dispatch(fetchEvents({ ticketUuid: ticket, ticketId, subdomain, limit: 20 }));
    p?.then?.((res) => {
      if (!res.payload.lastKey) {
        dispatch(fetchPreviousTickets({ ticketUuid: ticket, ticketId, subdomain }));
      }
    });
  }, [ticket, ticketId, subdomain, dispatch]);

  const hasNextPage = !dataWithPreviousTickets
    || !!lastKey
    || !previousTicketsFetchStatus
    || previousTicketsFetchStatus === ActionStatus.BUSY;
  const loader = useMemo(() => (hasNextPage
    && <UILoader message="Loading history..." />
  ), [hasNextPage]);

  return (
    <EventInfoContext.Provider value={{ activeError, updateError }}>
      <div className="events" style={{ height: '100%' }}>
        <BackToSetup />
        <div className="hide start-time">{Date.now()}</div>
        <ErrorDetailModal active={!!activeError} errorDescription={activeError} />

        <div id={`ticket-${ticket}`} className="container ticket-scroller" style={{ height: '100%' }}>
          <ReverseInfiniteList.Dynamic
            list={dataWithPreviousTickets}
            renderIds={renderIdsWithPreviousTickets}
            renderItem={renderEvent}
            isNextPageLoading={
              loadingStatus === ActionStatus.BUSY
              || previousTicketsFetchStatus === ActionStatus.BUSY
            }
            loader={loader}
            assumeSameRowHeight={false}
            loadNextPage={loadNextPage}
            stickyAppendMargin={110}
          />
        </div>
      </div>
    </EventInfoContext.Provider>
  );
};
